import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Save, GripVertical } from "lucide-react";
import { toast } from "sonner";

// Stage → output part-number mapping (Routing.stage_outputs), in process
// order. "raw_material" precedes the job pipeline — it just says which RAW
// item this FG starts from, defaulting the Issue dialog — and "finished"
// accepts FG or SFG (a sub-assembly's own Routing can point "finished" at a
// different SFG than its own item_id). A blank stage here means the
// Production Flow never creates a job at that stage for this part.
const ROUTING_STAGES = [
  { key: "raw_material",     label: "Raw Material",     types: ["RAW"] },
  { key: "machine_shop",     label: "Machine Shop",     types: ["MS"] },
  { key: "impregnation",     label: "Impregnation",     types: ["IMP"] },
  { key: "fabrication",      label: "Fabrication",      types: ["FAB"] },
  { key: "powder_coating",   label: "Powder Coating",   types: ["PWD"] },
  { key: "semi_finished",    label: "Semi Finished",    types: ["SFG"] },
  { key: "assembly",         label: "Assembly",         types: ["ASM"] },
  { key: "testing",          label: "Testing",          types: ["TEST"] },
  { key: "final_inspection", label: "Final Inspection", types: ["FI"] },
  { key: "finished",         label: "Finished Goods",   types: ["FG", "SFG"] },
];

export default function RoutingPage() {
  const [items, setItems] = useState([]);
  const [wcs, setWcs] = useState([]);
  const [selectedItem, setSelectedItem] = useState("");
  const [routing, setRouting] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      const [it, w] = await Promise.all([api.get("/items"), api.get("/workcenters")]);
      const fgs = it.data.filter((i) => i.type !== "RAW");
      setItems(it.data);
      setWcs(w.data);
      if (fgs.length) setSelectedItem(fgs[0].id);
    })();
  }, []);

  useEffect(() => {
    if (!selectedItem) return;
    (async () => {
      const { data } = await api.get(`/routings/by-item/${selectedItem}`);
      setRouting(data || { item_id: selectedItem, name: "Default", operations: [], stage_outputs: {} });
    })();
  }, [selectedItem]);

  const fgItems = useMemo(() => items.filter((i) => i.type !== "RAW"), [items]);
  const itemsByType = useMemo(() => {
    const map = {};
    items.forEach((i) => { (map[i.type] = map[i.type] || []).push(i); });
    return map;
  }, [items]);
  const itemMap = useMemo(() => Object.fromEntries(items.map((i) => [i.id, i])), [items]);

  const setStageOutput = (stageKey, itemId) => setRouting((r) => ({
    ...r,
    stage_outputs: { ...(r.stage_outputs || {}), [stageKey]: itemId },
  }));

  const addOp = () => {
    setRouting((r) => ({
      ...r,
      operations: [...(r.operations || []), {
        seq: ((r.operations || []).length + 1) * 10,
        workcenter_id: wcs[0]?.id || "",
        description: "",
        setup_time_min: 0,
        cycle_time_min: 0,
      }],
    }));
  };

  const setOp = (i, patch) => setRouting((r) => ({
    ...r,
    operations: r.operations.map((o, idx) => idx === i ? { ...o, ...patch } : o),
  }));

  const removeOp = (i) => setRouting((r) => ({
    ...r,
    operations: r.operations.filter((_, idx) => idx !== i),
  }));

  const save = async () => {
    const payload = {
      item_id: selectedItem,
      name: routing.name || "Default",
      operations: (routing.operations || []).map((o, idx) => ({ ...o, seq: o.seq || (idx + 1) * 10 })),
      stage_outputs: routing.stage_outputs || {},
    };
    setBusy(true);
    try {
      const { data } = await api.post("/routings", payload);
      setRouting(data);
      toast.success("Routing saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  const wcMap = useMemo(() => Object.fromEntries(wcs.map((w) => [w.id, w])), [wcs]);
  const totalTime = (routing?.operations || []).reduce((s, o) => s + (parseFloat(o.setup_time_min) || 0) + (parseFloat(o.cycle_time_min) || 0), 0);

  return (
    <div className="space-y-6" data-testid="routing-page">
      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Manufacturing</div>
        <h1 className="text-3xl sm:text-4xl tracking-tight font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>
          Routing Editor
        </h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-1 bg-white border border-slate-200 rounded-lg p-5">
          <Label className="text-xs uppercase tracking-[0.14em] text-slate-500">Finished Good</Label>
          <Select value={selectedItem} onValueChange={setSelectedItem}>
            <SelectTrigger className="mt-1.5" data-testid="routing-item-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {fgItems.map((it) => <SelectItem key={it.id} value={it.id}>{it.code} — {it.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="mt-6 text-xs text-slate-500 uppercase tracking-[0.14em]">Total Time per Unit</div>
          <div className="mt-1 text-3xl font-extrabold text-slate-900 mono">{totalTime.toFixed(1)} min</div>
          <Button onClick={save} disabled={busy} className="mt-6 w-full bg-blue-600 hover:bg-blue-700" data-testid="save-routing-btn">
            <Save className="w-4 h-4 mr-1.5" /> Save Routing
          </Button>
        </div>

        <div className="md:col-span-2 bg-white border border-slate-200 rounded-lg p-5">
          <h3 className="text-lg font-semibold text-slate-800 mb-1" style={{ fontFamily: "Manrope" }}>Stage Output Parts</h3>
          <p className="text-xs text-slate-500 mb-3">
            The part number this FG becomes at each stage. Leave a stage blank to skip it entirely — the Production Flow will never create a job there for this part.
          </p>
          <div className="space-y-2" data-testid="routing-stage-outputs">
            {ROUTING_STAGES.map((s) => {
              const options = s.types.flatMap((t) => itemsByType[t] || []);
              const value = (routing?.stage_outputs || {})[s.key] || "";
              const selected = itemMap[value];
              return (
                <div key={s.key} className="grid grid-cols-12 gap-2 items-center" data-testid={`routing-stage-row-${s.key}`}>
                  <Label className="col-span-4 text-xs text-slate-600">{s.label}</Label>
                  <div className="col-span-8">
                    <Select value={value || "__blank__"} onValueChange={(v) => setStageOutput(s.key, v === "__blank__" ? "" : v)}>
                      <SelectTrigger className="h-8 text-xs" data-testid={`routing-stage-select-${s.key}`}>
                        <SelectValue placeholder="— Skip this stage —" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__blank__">— Skip this stage —</SelectItem>
                        {options.map((it) => (
                          <SelectItem key={it.id} value={it.id} data-testid={`routing-stage-opt-${s.key}-${it.code}`}>{it.code} — {it.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {value && !selected && (
                      <p className="text-[10px] text-amber-600 mt-0.5">Previously mapped to an item that no longer matches type {s.types.join("/")}.</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="md:col-span-3 bg-white border border-slate-200 rounded-lg p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-slate-800" style={{ fontFamily: "Manrope" }}>Operations</h3>
            <Button variant="outline" size="sm" onClick={addOp} data-testid="add-op-btn"><Plus className="w-4 h-4 mr-1" /> Add Op</Button>
          </div>
          <div className="space-y-3">
            {(routing?.operations || []).length === 0 && (
              <div className="text-sm text-slate-500 py-8 text-center">No operations yet. Add the first one.</div>
            )}
            {(routing?.operations || []).map((op, idx) => (
              <div key={idx} className="border border-slate-200 rounded-md p-3 grid grid-cols-12 gap-2 items-end bg-slate-50/40" data-testid={`op-row-${idx}`}>
                <div className="col-span-1 flex items-center justify-center pb-2 text-slate-400">
                  <GripVertical className="w-4 h-4" />
                </div>
                <div className="col-span-1">
                  <Label className="text-[10px] uppercase">Seq</Label>
                  <Input type="number" value={op.seq} onChange={(e) => setOp(idx, { seq: parseInt(e.target.value || 0, 10) })} />
                </div>
                <div className="col-span-3">
                  <Label className="text-[10px] uppercase">Workcenter</Label>
                  <Select value={op.workcenter_id} onValueChange={(v) => setOp(idx, { workcenter_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {wcs.map((w) => <SelectItem key={w.id} value={w.id}>{w.code} — {w.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-4">
                  <Label className="text-[10px] uppercase">Description</Label>
                  <Input value={op.description} onChange={(e) => setOp(idx, { description: e.target.value })} />
                </div>
                <div className="col-span-1">
                  <Label className="text-[10px] uppercase">Setup (m)</Label>
                  <Input type="number" step="0.1" value={op.setup_time_min} onChange={(e) => setOp(idx, { setup_time_min: parseFloat(e.target.value || 0) })} />
                </div>
                <div className="col-span-1">
                  <Label className="text-[10px] uppercase">Cycle (m)</Label>
                  <Input type="number" step="0.1" value={op.cycle_time_min} onChange={(e) => setOp(idx, { cycle_time_min: parseFloat(e.target.value || 0) })} />
                </div>
                <div className="col-span-1">
                  <Button variant="ghost" size="icon" onClick={() => removeOp(idx)}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <h3 className="text-lg font-semibold text-slate-800 mb-3" style={{ fontFamily: "Manrope" }}>Workcenters</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {wcs.map((w) => (
            <div key={w.id} className="border border-slate-200 rounded-md p-3" data-testid={`wc-card-${w.code}`}>
              <div className="text-xs text-slate-500 uppercase tracking-[0.14em]">{w.code}</div>
              <div className="text-sm font-semibold text-slate-800 mt-1">{w.name}</div>
              <div className="text-xs text-slate-500 mt-1 mono">{w.capacity_per_hour}/hr · ₹{w.cost_per_hour}/hr</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
